'use client'

import {useEffect, useState} from 'react'

import SynchronizedZoomImage, {type SynchronizedZoomState} from './SynchronizedZoomImage'

export type MachineVisualizationPoint = {
    X: number
    Y: number
    Z: number
}

export type MachineVisualizationSection = {
    SectionName?: string
    Polylines?: MachineVisualizationPoint[][]
    SizeSection?: {
        X?: number
        Y?: number
    }
    // Centre of the section within the product, measured from the bottom of the product.
    LocationSection?: {
        X?: number
        Y?: number
    }
    // Converts the bottom-based LocationSection into the centre-based space this preview
    // draws in — the machine reports -Size.Y / 2 here.
    SectionOffsetY?: number
    // Polylines are exported 0,0-based; this is where the machine moves the artwork to.
    ArtOffset?: {
        X?: number
        Y?: number
        Z?: number
    }
}

export type MachineVisualizationProduct = {
    bundle_index?: string | number
    sku?: string
    Size?: {
        X?: number
        Y?: number
    }
    RevolvingPolyline?: [number, number, number][]
    ListSections?: MachineVisualizationSection[]
}

type SnapshotViewport = {
    width?: number
    height?: number
    product_margin?: number | null
}

type SectionGeometry = {
    width?: number
    height?: number
    viewport?: SnapshotViewport
}

type MachineImageProps = {
    section?: MachineVisualizationSection
    product?: MachineVisualizationProduct
    geometry: SectionGeometry
    zoom: SynchronizedZoomState
    onZoomChange: (zoom: SynchronizedZoomState) => void
}

type CanvasPoint = {
    x: number
    y: number
}

const DEFAULT_CANVAS_SIZE = 1000
const DEFAULT_PRODUCT_MARGIN = 50

const positiveNumber = (value: unknown, fallback: number) => {
    const number = Number(value)
    return Number.isFinite(number) && number > 0 ? number : fallback
}

const finiteNumber = (value: unknown, fallback: number) => {
    const number = Number(value)
    return Number.isFinite(number) ? number : fallback
}

const drawPath = (
    context: CanvasRenderingContext2D,
    points: CanvasPoint[],
    {
        stroke,
        fill,
        lineWidth,
        close = false,
    }: {
        stroke?: string
        fill?: string
        lineWidth: number
        close?: boolean
    }
) => {
    if (points.length === 0) return

    context.beginPath()
    points.forEach((point, index) => {
        if (index === 0) context.moveTo(point.x, point.y)
        else context.lineTo(point.x, point.y)
    })
    if (close) context.closePath()

    if (fill) {
        context.fillStyle = fill
        context.fill()
    }
    if (stroke) {
        context.lineWidth = lineWidth
        context.strokeStyle = stroke
        context.stroke()
    }
}

const createMachinePreview = (
    section: NonNullable<MachineImageProps['section']>,
    product: NonNullable<MachineImageProps['product']>,
    geometry: SectionGeometry
) => {
    const canvas = document.createElement('canvas')
    const canvasWidth = positiveNumber(geometry.viewport?.width, DEFAULT_CANVAS_SIZE)
    const canvasHeight = positiveNumber(geometry.viewport?.height, DEFAULT_CANVAS_SIZE)
    const configuredMargin = geometry.viewport?.product_margin
    const productMargin = configuredMargin == null
        ? DEFAULT_PRODUCT_MARGIN
        : Math.max(finiteNumber(configuredMargin, DEFAULT_PRODUCT_MARGIN), 0)

    canvas.width = canvasWidth
    canvas.height = canvasHeight

    const context = canvas.getContext('2d')
    if (!context) return ''

    const productWidth = positiveNumber(product.Size?.X, 1)
    const productHeight = positiveNumber(product.Size?.Y, 1)
    const availableWidth = Math.max(canvasWidth - productMargin * 2, 1)
    const availableHeight = Math.max(canvasHeight - productMargin * 2, 1)
    const productScale = Math.min(
        availableWidth / productWidth,
        availableHeight / productHeight
    )
    const canvasCenterX = canvasWidth / 2
    const canvasCenterY = canvasHeight / 2

    const toCanvasPoint = (x: number, y: number): CanvasPoint => ({
        x: canvasCenterX + x * productScale,
        y: canvasCenterY - y * productScale,
    })

    const productProfile = Array.isArray(product.RevolvingPolyline)
        ? product.RevolvingPolyline
        : []
    const productOutline = [
        ...productProfile.map((point) => (
            toCanvasPoint(
                finiteNumber(point?.[0], 0),
                finiteNumber(point?.[2], 0) - productHeight / 2
            )
        )),
        ...[...productProfile].reverse().map((point) => (
            toCanvasPoint(
                -finiteNumber(point?.[0], 0),
                finiteNumber(point?.[2], 0) - productHeight / 2
            )
        )),
    ]

    drawPath(context, productOutline, {
        fill: '#e9e9e9',
        lineWidth: 0,
        close: true,
    })

    // Placement comes from the machine visualization itself, mirroring how the machine's own
    // viewer draws it: the section rectangle is centred on LocationSection + SectionOffsetY, and
    // the artwork polylines are translated by ArtOffset. The order's `section_coordinates` is NOT
    // usable here — it is the customizer's offset within the engraving target object, so it is
    // measured from the bottom of the target area rather than the bottom of the product, and
    // mixing the two frames drops the section towards the base of the model.
    const sectionWidth = positiveNumber(
        section.SizeSection?.X,
        positiveNumber(geometry.width, productWidth)
    )
    const sectionHeight = positiveNumber(
        section.SizeSection?.Y,
        positiveNumber(geometry.height, productHeight)
    )
    const sectionCenterX = finiteNumber(section.LocationSection?.X, 0)
    const sectionCenterY = finiteNumber(section.LocationSection?.Y, 0)
        + finiteNumber(section.SectionOffsetY, 0)
    const halfSectionWidth = sectionWidth / 2
    const halfSectionHeight = sectionHeight / 2
    const sectionBorder = [
        toCanvasPoint(sectionCenterX - halfSectionWidth, sectionCenterY - halfSectionHeight),
        toCanvasPoint(sectionCenterX + halfSectionWidth, sectionCenterY - halfSectionHeight),
        toCanvasPoint(sectionCenterX + halfSectionWidth, sectionCenterY + halfSectionHeight),
        toCanvasPoint(sectionCenterX - halfSectionWidth, sectionCenterY + halfSectionHeight),
    ]

    drawPath(context, sectionBorder, {
        stroke: '#667085',
        lineWidth: 1.5,
        close: true,
    })

    const artOffsetX = finiteNumber(section.ArtOffset?.X, 0)
    const artOffsetY = finiteNumber(section.ArtOffset?.Y, 0)
    const polylines = Array.isArray(section.Polylines) ? section.Polylines : []
    polylines.forEach((line) => {
        if (!Array.isArray(line)) return

        drawPath(
            context,
            line.map((point) => (
                toCanvasPoint(
                    artOffsetX + finiteNumber(point?.X, 0),
                    artOffsetY + finiteNumber(point?.Y, 0)
                )
            )),
            {
                stroke: '#101828',
                lineWidth: 1,
            }
        )
    })

    return canvas.toDataURL('image/png')
}

const MachineImage = ({
    section,
    product,
    geometry,
    zoom,
    onZoomChange,
}: MachineImageProps) => {
    const [url, setUrl] = useState('')

    useEffect(() => {
        if (!section || !product) {
            setUrl('')
            return
        }

        setUrl(createMachinePreview(section, product, geometry))
    }, [
        geometry.height,
        geometry.viewport?.height,
        geometry.viewport?.product_margin,
        geometry.viewport?.width,
        geometry.width,
        product,
        section,
    ])

    if (!url) {
        return (
            <div className="flex h-[300px] w-[300px] items-center justify-center overflow-hidden rounded-md border border-gray-200">
                <img
                    src="/assets/images/Loading.gif"
                    alt="Loading machine-generated image"
                    className="h-[150px] w-[150px] object-cover"
                />
            </div>
        )
    }

    return (
        <SynchronizedZoomImage
            src={url}
            alt="Machine-generated section"
            zoom={zoom}
            onZoomChange={onZoomChange}
        />
    )
}

export default MachineImage
